import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import EduLogo from "../components/EduLogo";
import { api } from "../lib/api";

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [success, setSuccess] = useState(false);

    async function handleReset() {
        setErr("");
        if (password.length < 6) return setErr("La contraseña debe tener al menos 6 caracteres.");
        if (password !== confirm) return setErr("Las contraseñas no coinciden.");
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            setSuccess(true);
        } catch (e) {
            setErr(e.response?.data?.detail || "Error al restablecer la contraseña.");
        } finally {
            setLoading(false);
        }
    }

    if (!token) return (
        <div className="min-h-screen bg-edu-bg flex items-center justify-center p-4">
            <div className="edu-card p-8 text-center max-w-md w-full">
                <p className="text-red-600 font-medium">Enlace inválido.</p>
                <button onClick={() => navigate("/login")} className="mt-4 text-sm text-edu-navy hover:underline">
                    Volver al login
                </button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-edu-bg flex items-center justify-center p-4 relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-edu-gold/10 blur-3xl" />
                <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-edu-navy/10 blur-3xl" />
            </div>

            <div className="relative w-full max-w-md fade-in">
                <div className="flex justify-center mb-8">
                    <EduLogo size="lg" />
                </div>

                <div className="edu-card p-8 md:p-10">
                    {success ? (
                        <div className="text-center space-y-4">
                            <div className="text-4xl">✅</div>
                            <h2 className="text-xl font-semibold">¡Contraseña actualizada!</h2>
                            <p className="text-sm text-edu-body">Ya puedes iniciar sesión con tu nueva contraseña.</p>
                            <button
                                onClick={() => navigate("/login")}
                                className="edu-btn-primary w-full py-2.5 mt-2"
                            >
                                Ir al login
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <h2 className="text-2xl font-semibold text-center mb-2">Nueva contraseña</h2>
                            <p className="text-sm text-edu-body text-center mb-4">
                                Elige una contraseña segura de al menos 6 caracteres.
                            </p>

                            <div>
                                <label className="text-xs font-semibold uppercase tracking-widest text-edu-muted mb-1.5 block">
                                    Nueva contraseña
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPwd ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="edu-input w-full pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPwd((s) => !s)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-edu-muted"
                                    >
                                        {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-semibold uppercase tracking-widest text-edu-muted mb-1.5 block">
                                    Confirmar contraseña
                                </label>
                                <input
                                    type="password"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    placeholder="••••••••"
                                    className="edu-input w-full"
                                />
                            </div>

                            {err && (
                                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                    {err}
                                </div>
                            )}

                            <button
                                onClick={handleReset}
                                disabled={loading || !password || !confirm}
                                className="edu-btn-primary w-full py-3 font-semibold disabled:opacity-60"
                            >
                                {loading ? "Guardando…" : "Restablecer contraseña"}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
